<template>
  <div :class="$style.container" @click="handleClick">
    <bill-image-card :bill="bill" />

    <div :class="$style.content">
      <div :class="[$style.logo, { [$style.small]: isDeviceSize.mobile }]">
        <project-logo
          :logo-link="bill.projectLogoLink"
          :project-name="bill.projectName"
        />
      </div>

      <div
        v-if="bill.projectName"
        :class="$style.title"
        v-text="bill.projectName"
      />

      <div
        v-if="bill.description"
        :class="$style.description"
        :title="bill.description"
        v-text="bill.description"
      />

      <div
        v-if="isEmptyAmount"
        v-text="$t('common.support')"
      />
      <div
        v-else-if="isWidget"
        :class="$style.widget"
      >
        <div :class="$style.progress">
          <span v-text="displayCurrentProgress" />
          <span v-text="' / '" />
          <span v-text="displayWidgetAmount" />
        </div>
        <base-progress
          :value="getTariff.currentProgress"
          :max="getTariff.amount"
        />
      </div>
      <div
        v-else
        :class="$style.amount"
        v-text="displayAmount"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import BaseProgress from 'atoms/base/BaseProgress';
import ProjectLogo from 'molecules/project/ProjectLogo';
import BillImageCard from 'molecules/card/BillImageCard';

export default {
  name: 'BillCard',
  components: {
    BaseProgress,
    ProjectLogo,
    BillImageCard,
  },
  props: {
    bill: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isWidget() {
      return this.$validate.isObjectWithFields(this.getTariff, ['currentProgress']);
    },
    isEmptyAmount() {
      if (this.$validate.isNull(this.getTariff)) {
        return true;
      }

      if (!this.$validate.isObjectWithFields(this.getTariff, ['amount'])) {
        return true;
      }

      return this.$validate.isNull(this.getTariff.amount);
    },
    displayAmount() {
      const { billTariffs } = this.bill;

      if (this.$validate.isFixedLengthArray(billTariffs, 1)) {
        return formatAmount(this.getTariff.amount, this.getTariff.currency);
      }

      return this.$t('helper.amount.from', {
        amount: formatAmount(this.getTariff.amount, this.getTariff.currency),
      });
    },
    getTariff() {
      const { billTariffs } = this.bill;

      if (this.$validate.isEmptyArray(billTariffs)) {
        return null;
      }

      if (this.$validate.isFixedLengthArray(billTariffs, 1)) {
        const [tariff] = billTariffs;

        if (this.$validate.isNotObject(tariff)) {
          return null;
        }

        return tariff;
      }

      const amountList = billTariffs.map(tariff => tariff.amount);
      const minAmount = Math.min(...amountList.filter(amount => amount));
      const tariffWithMinAmount = billTariffs.find(tariff => tariff.amount === minAmount);

      return tariffWithMinAmount;
    },
    displayCurrentProgress() {
      if (!this.isWidget) {
        return null;
      }

      const {
        currency,
        currentProgress,
      } = this.getTariff;

      return formatAmount(currentProgress, {
        significant_zeros: currency.significant_zeros,
        type: currency.type,
      });
    },
    displayWidgetAmount() {
      if (!this.isWidget) {
        return null;
      }

      const {
        currency,
        amount,
      } = this.getTariff;

      return formatAmount(amount, currency);
    },
    ...mapGetters(['isDeviceSize']),
  },
  methods: {
    handleClick() {
      this.$router.replace({
        query: { widgetId: this.bill.widgetId },
      });

      this.$emit('click', this.bill);
    },
  },
};
</script>

<style lang="sass" module>
  .container
    +font-type(medium, 12, 15)
    border-radius: 5px
    background: rgba(255, 255, 255, 1)
    box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.15)
    overflow: hidden
    cursor: pointer

    +respond-to(small)
      +font-type(medium, 16, 20)

  .content
    position: relative
    padding: 15px

    +respond-to(small)
      padding: 15px 20px 20px 20px

  .logo
    +size(50px)
    position: absolute
    top: -25px
    right: 20px
    border: 2px solid rgba(255, 255, 255, 1)
    border-radius: 50%

    &.small
      right: 5px
      transform: scale(0.66)

  .title
    +font-type(regular, 12, 16)
    +text-ellipsis
    margin-bottom: 10px
    padding-right: 40px
    color: rgba(33, 33, 33, 0.5)

    +respond-to(small)
      +font-type(regular, 14, 20)
      margin-bottom: 15px

  .description
    +font-type(medium, 12, 16)
    margin-bottom: 10px
    color: rgb(33, 33, 33)
    overflow: hidden
    word-break: break-word
    display: -webkit-box
    -webkit-line-clamp: 2
    -webkit-box-orient: vertical

    +respond-to(small)
      +font-type(medium, 16, 20)
      margin-bottom: 15px

  .amount
    +font-type(regular, 18, 20)

  .widget
    +flexbox
    +flex-direction(column)

  .progress
    +font-type(regular, 12, 17)
    margin-bottom: 5px

    +respond-to(small)
      margin-bottom: 10px

    span
      &:nth-child(1),
      &:nth-child(3),
        white-space: nowrap
</style>
